import { useRef, useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Capacitor } from "@capacitor/core";
import { SpeechRecognition } from "@capacitor-community/speech-recognition";

interface UseSpeechRecognitionOptions {
  /** Called with each final transcript segment. Append or process as needed. */
  onTranscript: (text: string) => void;
  /** BCP 47 language tag for recognition. Defaults to 'en-IN'. */
  lang?: string;
}

/**
 * useWebSpeechRecognition
 *
 * Web Speech API voice input. Used on Android WebView (Capacitor) and desktop
 * Chrome. Not usable on iOS — WKWebView does not implement the Web Speech API,
 * so iOS goes through useNativeSpeechRecognition instead.
 *
 * Android-specific fixes applied here:
 *  1. Calls navigator.mediaDevices.getUserMedia({ audio: true }) before starting
 *     recognition. This triggers the Android OS-level RECORD_AUDIO runtime
 *     permission dialog, which is a separate layer from the WebView's
 *     onPermissionRequest handled in MainActivity.java.
 *  2. Uses continuous: false instead of continuous: true. Android WebView
 *     unreliably silences recognition mid-session with continuous: true.
 *     Instead, we simulate continuous mode by restarting in onend while
 *     isListeningRef is still true.
 *  3. 'no-speech' errors are silently ignored — onend fires and auto-restarts.
 */
function useWebSpeechRecognition({ onTranscript, lang = 'en-IN' }: UseSpeechRecognitionOptions) {
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef<any>(null);

  // Refs to avoid stale closures in recognition event handlers
  const isListeningRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const langRef = useRef(lang);

  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { langRef.current = lang; }, [lang]);

  // Abort recognition and clean up on unmount
  useEffect(() => {
    return () => {
      isListeningRef.current = false;
      recognitionRef.current?.abort();
    };
  }, []);

  /** Creates a fresh SpeechRecognition instance and starts it. */
  const createAndStart = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) return;

    const recognition = new SpeechRecognition();
    recognition.lang = langRef.current;
    recognition.interimResults = false;
    // continuous: false is more reliable on Android WebView.
    // We simulate continuous input by restarting in onend.
    recognition.continuous = false;
    recognition.maxAlternatives = 1;

    recognition.onresult = (event: any) => {
      let transcript = '';
      for (let i = event.resultIndex; i < event.results.length; i++) {
        if (event.results[i].isFinal) {
          transcript += event.results[i][0].transcript;
        }
      }
      if (transcript) {
        onTranscriptRef.current(transcript);
      }
    };

    recognition.onerror = (event: any) => {
      console.error('Speech recognition error:', event.error);
      const fatalErrors = ['not-allowed', 'service-not-allowed', 'network'];
      if (event.error === 'not-allowed' || event.error === 'service-not-allowed') {
        toast.error("Microphone access denied. Please allow microphone permission in your device settings.");
        isListeningRef.current = false;
        setIsListening(false);
      } else if (event.error === 'network') {
        toast.error("Network error. Voice input requires an internet connection.");
        isListeningRef.current = false;
        setIsListening(false);
      } else if (event.error === 'no-speech') {
        // Silently ignore — onend will restart if still listening
      } else if (!fatalErrors.includes(event.error)) {
        toast.error("Voice input error. Please try again.");
        isListeningRef.current = false;
        setIsListening(false);
      }
    };

    recognition.onend = () => {
      // If still supposed to be listening, restart to simulate continuous mode.
      // This is the key fix for Android WebView where continuous: true drops silently.
      // A 100ms delay is required: calling recognition.start() immediately in onend
      // throws InvalidStateError on Android because the engine hasn't fully reset yet.
      if (isListeningRef.current) {
        setTimeout(() => {
          if (isListeningRef.current) {
            try {
              recognition.start();
            } catch {
              isListeningRef.current = false;
              setIsListening(false);
            }
          }
        }, 100);
      }
    };

    recognitionRef.current = recognition;
    recognition.start();
  }, []);

  const toggleVoiceInput = useCallback(async () => {
    if (isListeningRef.current) {
      // — STOP —
      isListeningRef.current = false;
      setIsListening(false);
      recognitionRef.current?.abort();
      return;
    }

    // — START —
    // Check browser support first
    const SpeechRecognition =
      (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      toast.error("Voice input is not supported in this browser");
      return;
    }

    // Request getUserMedia to trigger the Android OS-level RECORD_AUDIO permission dialog.
    // On desktop browsers this is a no-op / quick grant since mic is already trusted.
    if (navigator.mediaDevices?.getUserMedia) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        // Stop the stream immediately — we only needed it for the permission prompt.
        stream.getTracks().forEach(track => track.stop());
      } catch (err: any) {
        const denied =
          err.name === 'NotAllowedError' ||
          err.name === 'PermissionDeniedError' ||
          err.name === 'SecurityError';
        if (denied) {
          toast.error("Microphone access denied. Please allow microphone permission in your device settings.");
          return;
        }
        // Other errors (device not found, etc.) — fall through; SpeechRecognition
        // may still work on some browsers even without getUserMedia.
      }
    }

    isListeningRef.current = true;
    setIsListening(true);
    createAndStart();
  }, [createAndStart]);

  return { isListening, toggleVoiceInput };
}

/**
 * useNativeSpeechRecognition
 *
 * iOS-only path, via @capacitor-community/speech-recognition (SFSpeechRecognizer).
 * WKWebView has no Web Speech API, so there is nothing to fall back to on iOS.
 *
 * Mirrors the Web Speech path's contract: exactly ONE final string per utterance.
 * That matters because every consumer *appends* what it receives — emitting the
 * plugin's `partialResults` (which re-emit the whole cumulative hypothesis on each
 * update: "hello" → "hello there" → "hello there world") would duplicate text badly.
 *
 * With partialResults: false the plugin sets shouldReportPartialResults = false, so
 * SFSpeech invokes its handler once at end-of-utterance and start() resolves with the
 * final matches. Sessions are capped at ~1 minute by iOS and end on silence, so we
 * restart in a loop while still listening — the same trick the Android path uses in
 * its onend handler, just relocated.
 */
function useNativeSpeechRecognition({ onTranscript, lang = 'en-IN' }: UseSpeechRecognitionOptions) {
  const [isListening, setIsListening] = useState(false);

  // Refs to avoid stale closures across the restart loop
  const isListeningRef = useRef(false);
  const onTranscriptRef = useRef(onTranscript);
  const langRef = useRef(lang);

  useEffect(() => { onTranscriptRef.current = onTranscript; }, [onTranscript]);
  useEffect(() => { langRef.current = lang; }, [lang]);

  // Stop recognition and clean up on unmount
  useEffect(() => {
    return () => {
      isListeningRef.current = false;
      SpeechRecognition.stop().catch(() => { /* not listening — nothing to stop */ });
    };
  }, []);

  /** Listens for a single utterance, emits it, then restarts if still listening. */
  const runOnce = useCallback(async () => {
    try {
      const result = await SpeechRecognition.start({
        language: langRef.current,
        maxResults: 1,
        partialResults: false,
        popup: false,
      });
      const transcript = result?.matches?.[0];
      if (transcript) {
        onTranscriptRef.current(transcript);
      }
    } catch (err) {
      // A silence timeout with no speech surfaces as a rejection here. Mirror the
      // Web Speech 'no-speech' branch: ignore it and let the loop restart.
      console.warn('Speech recognition ended:', err);
    }

    if (isListeningRef.current) {
      runOnce();
    }
  }, []);

  const toggleVoiceInput = useCallback(async () => {
    if (isListeningRef.current) {
      // — STOP —
      // Clear the flag first so the in-flight runOnce() doesn't restart. Its pending
      // promise still resolves with whatever was said, so the transcript isn't lost.
      isListeningRef.current = false;
      setIsListening(false);
      await SpeechRecognition.stop().catch(() => { /* already stopped */ });
      return;
    }

    // — START —
    const { available } = await SpeechRecognition.available();
    if (!available) {
      toast.error("Voice input is not supported on this device");
      return;
    }

    // One alias covers both SFSpeechRecognizer and microphone access, so this needs
    // NSSpeechRecognitionUsageDescription and NSMicrophoneUsageDescription in Info.plist.
    let permission = await SpeechRecognition.checkPermissions();
    if (permission.speechRecognition !== 'granted') {
      permission = await SpeechRecognition.requestPermissions();
    }
    if (permission.speechRecognition !== 'granted') {
      toast.error("Microphone access denied. Please allow microphone permission in your device settings.");
      return;
    }

    isListeningRef.current = true;
    setIsListening(true);
    runOnce();
  }, [runOnce]);

  return { isListening, toggleVoiceInput };
}

/**
 * useSpeechRecognition
 *
 * Shared voice-input hook. Picks the native SFSpeechRecognizer path on iOS and the
 * Web Speech API everywhere else (Android WebView, desktop browsers). Both variants
 * expose the same { isListening, toggleVoiceInput } interface and the same
 * one-final-transcript-per-utterance contract, so consumers need no platform logic.
 */
export function useSpeechRecognition(options: UseSpeechRecognitionOptions) {
  const isIOS = Capacitor.getPlatform() === 'ios';

  // Both hooks are called unconditionally (Rules of Hooks). Each is inert until
  // toggleVoiceInput() runs, so the unused one only costs a few refs and a no-op
  // cleanup — neither touches a mic or plugin until invoked.
  const web = useWebSpeechRecognition(options);
  const native = useNativeSpeechRecognition(options);

  return isIOS ? native : web;
}
